import Loader from 'components/Common/Loader';
import { S3_SMALL_THUMB, S3_URL } from 'constants';
import { IMAGE_PATHS } from 'constants/imagePaths';
import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Button, Image, Stack } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import useAuthStore from 'store/auth';
import useChatStore, { setChatUser, setRoomId } from 'store/chat';
import { setModalConfig } from 'store/common';
import { downloadFile, formatTime, getFileName, getFileNameByURL } from 'utils';
import parse from 'html-react-parser';
import { useNavigate, useParams } from 'react-router-dom';
import { seenMessage } from 'store/socket';
import { isMobileOnly } from 'react-device-detect';
import './styles.scss';
import useTranslate from 'hooks/useTranslate';

const ChatMessages: React.FC<any> = ({ receiverDetails, setCurrentPage, metaData }) => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { chatMessages }: Record<string, any> = useChatStore((state) => state || []);
	const { receiverId, roomId = '' } = useParams<string>();
	const [hasMore, setHasMore] = useState(metaData?.hasNextPage);
	const currentMediaRef = useRef<HTMLMediaElement | null>(null);

	const groupedMessages = useMemo(() => {
		if (!chatMessages || chatMessages.length === 0) return {};

		const sortedMessages = chatMessages?.sort(
			(a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime(),
		);

		const groups = sortedMessages.reduce((acc: any, message: any) => {
			const messageDate = new Date(message.time).toDateString();
			if (!acc[messageDate]) {
				acc[messageDate] = [];
			}
			acc[messageDate].push(message);
			return acc;
		}, {});

		return groups;
	}, [chatMessages]);

	useEffect(() => {
		if (isMobileOnly && roomId) {
			seenMessage(String(roomId));
			setChatUser(Number(receiverId));
			setRoomId(Number(roomId));
		}
	}, [roomId]);

	useEffect(() => {
		setHasMore(metaData?.hasNextPage || false);
	}, [metaData]);

	const loadMoreFunc = () => {
		if (hasMore) {
			setCurrentPage((prev: any) => prev + 1);
		}
	};

	const onAssetsClick = (mediaItem: any) => {
		setModalConfig({
			visible: true,
			data: [mediaItem],
			type: 'carouselModal',
			props: {
				fullscreen: true,
				centered: true,
				dialogClassName: 'dialog-assets-carousel',
			},
		});
	};

	const handleMediaPlay = (mediaElement: HTMLMediaElement) => {
		if (currentMediaRef.current && currentMediaRef.current !== mediaElement) {
			currentMediaRef.current.pause();
			currentMediaRef.current.currentTime = 0;
		}
		currentMediaRef.current = mediaElement;
	};

	const goToProfile = (receiverDetails: any) => {
		navigate(`/${receiverDetails?.userType}/profile/${receiverDetails?.id}`);
	};

	return (
		<Stack className="chat-preview">
			{chatMessages?.length > 0 && (
				<div
					id="infiniteDiv-msg-list"
					className="chat-preview-inner"
					style={{
						width: '100%',
						overflowY: 'auto',
						margin: 'auto',
						display: 'flex',
						flexDirection: 'column-reverse',
					}}
				>
					<InfiniteScroll
						dataLength={chatMessages?.length}
						next={loadMoreFunc}
						hasMore={metaData?.hasNextPage}
						loader={
							chatMessages?.length >= 1 && (
								<div className="d-flex justify-content-center align-items-center">
									<Loader />
								</div>
							)
						}
						scrollableTarget="infiniteDiv-msg-list"
						inverse={true}
					>
						{groupedMessages &&
							Object.keys(groupedMessages).map((groupLabel) => (
								<React.Fragment key={groupLabel}>
									<div className="chat-day text-center">
										<p className="mb-0">{groupLabel}</p>
									</div>
									{groupedMessages[groupLabel].map((message: any, indexId: number) => {
										const profileImg =
											message?.senderId === authUser?.userId
												? authUser?.profilePicture
												: receiverDetails?.profilePicture;
										return (
											<div
												key={indexId}
												className={`chat-preview-msg d-flex gap-1 gap-lg-2 ${message?.senderId === authUser?.userId ? 'sent-msg' : ''}`}
											>
												<div className="chat-person-img">
													<Image
														onClick={() => {
															goToProfile(receiverDetails);
														}}
														style={{ cursor: 'pointer' }}
														src={profileImg ? S3_URL + profileImg : IMAGE_PATHS?.UserDefault}
														alt="profile"
														className="w-100 h-100 object-fit-cover rounded-circle"
														loading="lazy"
													/>
												</div>
												<Stack className="chat-preview-msg-list flex-1">
													{message?.messageType === 'text' && (
														<div className="chat-msg-box d-flex align-items-end">
															{message?.message && (
																<p className="mb-0 flex-1">
																	{parse(message?.message.replace(/\n/g, '<br>'))}
																</p>
															)}
															<div className="chat-msg-time lh-1">{formatTime(message?.time)}</div>
														</div>
													)}
													{message?.messageType === 'image' && (
														<div className="chat-msg-box image-msg d-flex align-items-center">
															<div
																className="image-box position-relative"
																onClick={() =>
																	onAssetsClick({
																		mediaType: message?.messageType,
																		file: message?.message,
																		videoThumbnail: message?.videoThumbnail,
																	})
																}
															>
																<Image
																	src={S3_URL + message?.message}
																	alt="image"
																	className="w-100 h-100 object-fit-cover"
																	loading="lazy"
																/>
																<div className="chat-msg-time lh-1">
																	{formatTime(message?.time)}
																</div>
															</div>
														</div>
													)}
													{message?.messageType === 'video' && (
														<div className="chat-msg-box image-msg d-flex align-items-center">
															<div
																className="image-box video position-relative"
																onClick={() =>
																	onAssetsClick({
																		mediaType: message?.messageType,
																		file: message?.message,
																		videoThumbnail: message?.videoThumbnail,
																	})
																}
															>
																<Image
																	src={S3_SMALL_THUMB + getFileName(message?.message)}
																	alt="image"
																	className="w-100 h-100 object-fit-cover"
																	loading="lazy"
																/>
																<div className="chat-msg-time lh-1">
																	{formatTime(message?.time)}
																</div>
																<div className="play-icon-btn position-absolute top-0 start-0 w-100 h-100">
																	<i className="ri-play-circle-fill"></i>
																</div>
															</div>
														</div>
													)}

													{message?.messageType === 'audio' && (
														<div className="chat-msg-box audio">
															<div className="audio-box d-flex align-items-center">
																<audio
																	controls
																	onPlay={(e) => handleMediaPlay(e.target as HTMLMediaElement)}
																>
																	<source src={S3_URL + message?.message} type="audio/mpeg" />
																	{translate('Form.audioFile')}
																</audio>
															</div>
															<div className="chat-msg-time text-end lh-1 mt-2">
																{formatTime(message?.time)}
															</div>
														</div>
													)}

													{message?.messageType === 'doc' && (
														<div className="chat-msg-box document-msg d-flex align-items-center">
															<div className="document-box p-2 p-sm-3 d-flex align-items-center gap-1 gap-sm-3 position-relative">
																<div className="document-icon">
																	<i className="ri-file-text-line"></i>
																</div>
																<div className="flex-1">
																	<p className="document-name mb-0">
																		{getFileNameByURL(message?.message)}
																	</p>
																	<Button
																		className="document-info position-absolute"
																		onClick={() => {
																			downloadFile(
																				S3_URL + message?.message,
																				getFileNameByURL(message?.message),
																			);
																		}}
																	>
																		<i className="ri-download-fill"></i>
																	</Button>
																</div>
															</div>
															<div className="chat-msg-time lh-1">{formatTime(message?.time)}</div>
														</div>
													)}
												</Stack>
											</div>
										);
									})}
								</React.Fragment>
							))}
					</InfiniteScroll>
				</div>
			)}
		</Stack>
	);
};

export default ChatMessages;
